// src/components/dashboard/AdminAddEvent.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { db } from "../../firebase/config";
import { collection, addDoc, getDocs } from "firebase/firestore";
import { 
  Calendar, 
  Clock, 
  User, 
  Users, 
  MapPin, 
  FileText, 
  Sparkles,
  ArrowLeft,
  Save,
  Download,
  Palette,
  Star
} from "lucide-react";

// Couleurs et styles par rôle admin
const ADMIN_STYLES = {
  'Super Admin': {
    gradient: 'from-purple-600 via-purple-700 to-indigo-800',
    color: '#8B5CF6',
    lightBg: 'bg-purple-50',
    darkBg: 'bg-purple-900/20',
    badge: 'bg-purple-100 text-purple-800',
    icon: '👑'
  },
  'Chef de Projet': {
    gradient: 'from-emerald-500 via-teal-600 to-cyan-700',
    color: '#10B981',
    lightBg: 'bg-emerald-50',
    darkBg: 'bg-emerald-900/20',
    badge: 'bg-emerald-100 text-emerald-800',
    icon: '🎯'
  },
  'Chargé d\'Affaire': {
    gradient: 'from-orange-500 via-amber-500 to-yellow-600',
    color: '#F59E0B',
    lightBg: 'bg-orange-50',
    darkBg: 'bg-orange-900/20',
    badge: 'bg-orange-100 text-orange-800',
    icon: '⚡'
  },
  'Admin': {
    gradient: 'from-blue-600 via-blue-700 to-indigo-800',
    color: '#3B82F6',
    lightBg: 'bg-blue-50',
    darkBg: 'bg-blue-900/20',
    badge: 'bg-blue-100 text-blue-800',
    icon: '🚀'
  }
};

const EVENT_TYPES = [
  { value: 'visite-technique', label: 'Visite technique préliminaire', icon: '🔍', color: 'text-blue-600' },
  { value: 'installation', label: 'Installation des panneaux', icon: '🔧', color: 'text-green-600' },
  { value: 'mise-en-service', label: 'Mise en service', icon: '⚡', color: 'text-yellow-600' },
  { value: 'maintenance', label: 'Maintenance', icon: '🛠️', color: 'text-orange-600' },
  { value: 'rdv-client', label: 'Rendez-vous client', icon: '🤝', color: 'text-purple-600' },
  { value: 'controle', label: 'Contrôle qualité', icon: '✅', color: 'text-emerald-600' },
  { value: 'autre', label: 'Autre', icon: '📋', color: 'text-gray-600' }
];

const EVENT_STATUS = [
  { value: 'a-programmer', label: 'À programmer', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
  { value: 'planifie', label: 'Planifié', color: 'bg-blue-100 text-blue-800', icon: '📅' },
  { value: 'en-cours', label: 'En cours', color: 'bg-orange-100 text-orange-800', icon: '🔄' },
  { value: 'termine', label: 'Terminé', color: 'bg-green-100 text-green-800', icon: '✅' },
  { value: 'reporte', label: 'Reporté', color: 'bg-red-100 text-red-800', icon: '⏸️' }
];

const AdminAddEvent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const themeContext = useTheme();
  const theme = themeContext?.theme || 'light';
  
  const [loading, setLoading] = useState(false);
  const [dossiers, setDossiers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);

  const [eventData, setEventData] = useState({
    title: "",
    description: "",
    eventType: "visite-technique",
    status: "a-programmer",
    dossierId: "",
    clientName: "",
    responsableId: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    allDay: false,
    color: "#8B5CF6",
    location: "",
    notes: ""
  });

  // Chargement des données
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Charger les dossiers avec les noms de clients
        const dossiersSnapshot = await getDocs(collection(db, "dossiers"));
        const dossiersData = dossiersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          clientName: doc.data().client || doc.data().nomClient || doc.data().clientName || "Client inconnu"
        }));
        setDossiers(dossiersData);

        // Charger les admins
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const adminUsers = usersData.filter(user => 
          ['Super Admin', 'Chef de Projet', 'Chargé d\'Affaire', 'Admin', 'admin', 'superadmin', 'projectmanager', 'charge-affaire'].includes(user.role)
        );
        setAdmins(adminUsers);

        // Trouver les données de l'utilisateur actuel
        const currentUser = usersData.find(u => u.id === user?.uid);
        setCurrentUserData(currentUser);

        // Pré-remplir le responsable avec l'utilisateur actuel
        if (currentUser) {
          setEventData(prev => ({
            ...prev,
            responsableId: currentUser.id,
            color: ADMIN_STYLES[currentUser.role]?.color || "#8B5CF6"
          }));
        }

      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user]);

  // Fonction pour normaliser les rôles
  const normalizeRole = (role) => {
    const roleMap = {
      'admin': 'Admin',
      'superadmin': 'Super Admin',
      'projectmanager': 'Chef de Projet',
      'charge-affaire': 'Chargé d\'Affaire'
    };
    return roleMap[role] || role;
  };

  // Gestionnaire de changement pour la sélection de dossier
  const handleDossierChange = (dossierId) => {
    const selectedDossier = dossiers.find(d => d.id === dossierId);
    setEventData(prev => ({
      ...prev,
      dossierId: dossierId,
      clientName: selectedDossier?.clientName || "",
      location: selectedDossier?.adresse || selectedDossier?.adresseClient || ""
    }));
  };

  // Gestionnaire de changement pour le responsable
  const handleResponsableChange = (responsableId) => {
    const selectedAdmin = admins.find(a => a.id === responsableId);
    const normalizedRole = normalizeRole(selectedAdmin?.role);
    setEventData(prev => ({
      ...prev,
      responsableId: responsableId,
      color: ADMIN_STYLES[normalizedRole]?.color || "#8B5CF6"
    }));
  };

  // Génération d'un fichier .ics pour Google Calendar
  const generateICSFile = (event) => {
    const startDateTime = new Date(`${event.startDate}T${event.startTime || '09:00'}`);
    const endDateTime = event.endDate && event.endTime 
      ? new Date(`${event.endDate}T${event.endTime}`) 
      : new Date(startDateTime.getTime() + 60 * 60 * 1000); // +1 heure par défaut

    const formatDate = (date) => {
      return date.toISOString().replace(/[:\-]|\.\d{3}/g, '');
    };

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//EnergeSync//Calendar//FR',
      'BEGIN:VEVENT',
      `UID:${Date.now()}@energesync.com`,
      `DTSTAMP:${formatDate(new Date())}`,
      `DTSTART:${formatDate(startDateTime)}`,
      `DTEND:${formatDate(endDateTime)}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description}\\n\\nClient: ${event.clientName}\\n\\nNotes: ${event.notes}`,
      event.location ? `LOCATION:${event.location}` : '',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line !== '').join('\r\n');

    return icsContent;
  };

  // Sauvegarde de l'événement
  const handleSaveEvent = async () => {
    if (!eventData.title || !eventData.dossierId || !eventData.startDate || !eventData.responsableId) {
      alert("Veuillez remplir tous les champs obligatoires !");
      return;
    }

    setLoading(true);
    try {
      const startDateTime = new Date(`${eventData.startDate}T${eventData.startTime || '09:00'}`);
      const endDateTime = eventData.endDate && eventData.endTime 
        ? new Date(`${eventData.endDate}T${eventData.endTime}`) 
        : new Date(startDateTime.getTime() + 60 * 60 * 1000);

      const newEvent = {
        title: eventData.title,
        description: eventData.description,
        eventType: eventData.eventType,
        status: eventData.status,
        dossierId: eventData.dossierId,
        clientName: eventData.clientName,
        responsableId: eventData.responsableId,
        createdBy: user.uid,
        start: startDateTime,
        end: endDateTime,
        allDay: eventData.allDay,
        color: eventData.color,
        location: eventData.location,
        notes: eventData.notes,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, "events"), newEvent);
      
      // Générer et télécharger le fichier .ics
      const icsContent = generateICSFile(eventData);
      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${eventData.title.replace(/\s+/g, '_')}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert("Événement créé avec succès ! Le fichier .ics a été téléchargé pour l'ajouter à Google Calendar.");
      navigate("/admin/calendar");
    } catch (error) {
      console.error("Erreur lors de la création de l'événement:", error);
      alert("Erreur lors de la création de l'événement. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const currentAdminStyle = currentUserData 
    ? ADMIN_STYLES[normalizeRole(currentUserData.role)] 
    : ADMIN_STYLES['Admin'];

  if (loading && dossiers.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
      }`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className={theme === 'dark' ? 'text-white' : 'text-gray-600'}>Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 transition-all duration-300 ${
      theme === 'dark' 
        ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'
    }`}>
      <div className="max-w-4xl mx-auto">
        {/* Header avec animations */}
        <div className="mb-8 relative">
          <button
            onClick={() => navigate("/admin/calendar")}
            className={`mb-4 flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${
              theme === 'dark' 
                ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                : 'bg-white hover:bg-gray-50 text-gray-700 shadow-md hover:shadow-lg'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour au calendrier</span>
          </button>

          <div className={`relative overflow-hidden rounded-2xl p-8 ${
            theme === 'dark' ? 'bg-gray-800/50 backdrop-blur' : 'bg-white/70 backdrop-blur'
          } shadow-2xl border border-purple-200/20`}>
            <div className={`absolute inset-0 bg-gradient-to-r ${currentAdminStyle.gradient} opacity-10`}></div>
            <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 rounded-full bg-gradient-to-r ${currentAdminStyle.gradient} text-white shadow-lg`}>
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    ✨ Créer un événement
                  </h1>
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    Organisez vos interventions avec style
                  </p>
                </div>
              </div>
              
              {currentUserData && (
                <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${currentAdminStyle.badge}`}>
                  <span className="text-lg">{currentAdminStyle.icon}</span>
                  <span className="font-medium">{normalizeRole(currentUserData.role)}</span>
                  <span>•</span>
                  <span>{currentUserData.displayName || currentUserData.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Formulaire principal */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="lg:col-span-2 space-y-6">
            {/* Informations générales */}
            <div className={`rounded-2xl p-6 shadow-xl transition-all duration-300 hover:shadow-2xl ${
              theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
            }`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Informations générales
                </h2>
              </div>

              <div className="space-y-4">
                {/* Titre */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Titre de l'événement *
                  </label>
                  <input
                    type="text"
                    value={eventData.title}
                    onChange={(e) => setEventData(prev => ({ ...prev, title: e.target.value }))}
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Ex: Visite technique chez M. Dupont"
                  />
                </div>

                {/* Type et Statut */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Type d'intervention *
                    </label>
                    <select
                      value={eventData.eventType}
                      onChange={(e) => setEventData(prev => ({ ...prev, eventType: e.target.value }))}
                      className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {EVENT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Statut *
                    </label>
                    <select
                      value={eventData.status}
                      onChange={(e) => setEventData(prev => ({ ...prev, status: e.target.value }))}
                      className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      {EVENT_STATUS.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.icon} {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description
                  </label>
                  <textarea
                    value={eventData.description}
                    onChange={(e) => setEventData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Détails de l'intervention..."
                  />
                </div>
              </div>
            </div>

            {/* Assignation */}
            <div className={`rounded-2xl p-6 shadow-xl transition-all duration-300 hover:shadow-2xl ${
              theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
            }`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Client et responsable
                </h2>
              </div>

              <div className="space-y-4">
                {/* Sélection du dossier/client */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Client concerné *
                  </label>
                  <select
                    value={eventData.dossierId}
                    onChange={(e) => handleDossierChange(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Sélectionner un client...</option>
                    {dossiers.map(dossier => (
                      <option key={dossier.id} value={dossier.id}>
                        👤 {dossier.clientName} {dossier.titre ? `- ${dossier.titre}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Responsable */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Responsable de l'intervention *
                  </label>
                  <select
                    value={eventData.responsableId}
                    onChange={(e) => handleResponsableChange(e.target.value)}
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Sélectionner un responsable...</option>
                    {admins.map(admin => {
                      const normalizedRole = normalizeRole(admin.role);
                      const adminStyle = ADMIN_STYLES[normalizedRole];
                      return (
                        <option key={admin.id} value={admin.id}>
                          {adminStyle?.icon || '👤'} {admin.displayName || admin.email} ({normalizedRole})
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Localisation */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Lieu de l'intervention
                  </label>
                  <div className="relative">
                    <MapPin className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={eventData.location}
                      onChange={(e) => setEventData(prev => ({ ...prev, location: e.target.value }))}
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Adresse de l'intervention..."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne de droite */}
          <div className="space-y-6">
            {/* Planning */}
            <div className={`rounded-2xl p-6 shadow-xl transition-all duration-300 hover:shadow-2xl ${
              theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
            }`}>
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/50">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Planning
                </h2>
              </div>

              <div className="space-y-4">
                {/* Date de début */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Date de début *
                  </label>
                  <input
                    type="date"
                    value={eventData.startDate}
                    onChange={(e) => setEventData(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>

                {/* Heure de début */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Heure de début
                  </label>
                  <div className="relative">
                    <Clock className={`absolute left-3 top-3 w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} />
                    <input
                      type="time"
                      value={eventData.startTime}
                      onChange={(e) => setEventData(prev => ({ ...prev, startTime: e.target.value }))}
                      className={`w-full pl-12 pr-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        theme === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>
                </div>

                {/* Date de fin */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={eventData.endDate}
                    onChange={(e) => setEventData(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                      theme === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                       : 'bg-white border-gray-300 text-gray-900'
                   }`}
                 />
               </div>

               {/* Heure de fin */}
               <div>
                 <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                   Heure de fin
                 </label>
                 <input
                   type="time"
                   value={eventData.endTime}
                   onChange={(e) => setEventData(prev => ({ ...prev, endTime: e.target.value }))}
                   className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                     theme === 'dark' 
                       ? 'bg-gray-700 border-gray-600 text-white' 
                       : 'bg-white border-gray-300 text-gray-900'
                   }`}
                 />
               </div>

               {/* Journée entière */}
               <div className="flex items-center space-x-3">
                 <input
                   type="checkbox"
                   id="allDay"
                   checked={eventData.allDay}
                   onChange={(e) => setEventData(prev => ({ ...prev, allDay: e.target.checked }))}
                   className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                 />
                 <label htmlFor="allDay" className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                   Journée entière
                 </label>
               </div>
             </div>
           </div>

           {/* Personnalisation */}
           <div className={`rounded-2xl p-6 shadow-xl transition-all duration-300 hover:shadow-2xl ${
             theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
           }`}>
             <div className="flex items-center space-x-3 mb-6">
               <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
                 <Palette className="w-5 h-5 text-purple-600" />
               </div>
               <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                 Personnalisation
               </h2>
             </div>

             <div className="space-y-4">
               {/* Couleur */}
               <div>
                 <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                   Couleur de l'événement
                 </label>
                 <div className="flex items-center space-x-3">
                   <input
                     type="color"
                     value={eventData.color}
                     onChange={(e) => setEventData(prev => ({ ...prev, color: e.target.value }))}
                     className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                   />
                   <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                     {eventData.color}
                   </span>
                 </div>
               </div>

               {/* Notes */}
               <div>
                 <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                   Notes privées
                 </label>
                 <textarea
                   value={eventData.notes}
                   onChange={(e) => setEventData(prev => ({ ...prev, notes: e.target.value }))}
                   rows={3}
                   className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
                     theme === 'dark' 
                       ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                       : 'bg-white border-gray-300 text-gray-900'
                   }`}
                   placeholder="Notes internes..."
                 />
               </div>
             </div>
           </div>

           {/* Aperçu du statut */}
           {eventData.status && (
             <div className={`rounded-2xl p-6 shadow-xl transition-all duration-300 hover:shadow-2xl ${
               theme === 'dark' ? 'bg-gray-800/50 backdrop-blur border border-gray-700' : 'bg-white/70 backdrop-blur border border-white'
             }`}>
               <div className="flex items-center space-x-3 mb-4">
                 <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/50">
                   <Star className="w-5 h-5 text-yellow-600" />
                 </div>
                 <h3 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                   Aperçu
                 </h3>
               </div>

               <div className="space-y-3">
                 {eventData.clientName && (
                   <div className="flex items-center space-x-2">
                     <User className="w-4 h-4 text-gray-500" />
                     <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                       {eventData.clientName}
                     </span>
                   </div>
                 )}
                 
                 <div className="flex items-center space-x-2">
                   <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                     EVENT_STATUS.find(s => s.value === eventData.status)?.color || 'bg-gray-100 text-gray-800'
                   }`}>
                     {EVENT_STATUS.find(s => s.value === eventData.status)?.icon} {EVENT_STATUS.find(s => s.value === eventData.status)?.label}
                   </span>
                 </div>

                 <div className="flex items-center space-x-2">
                   <div 
                     className="w-4 h-4 rounded-full border-2 border-white shadow"
                     style={{ backgroundColor: eventData.color }}
                   ></div>
                   <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                     Couleur assignée
                   </span>
                 </div>
               </div>
             </div>
           )}
         </div>
       </div>

       {/* Boutons d'action */}
       <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
         <button
           onClick={() => navigate("/admin/calendar")}
           className={`px-8 py-4 rounded-xl font-medium transition-all duration-200 hover:scale-105 ${
             theme === 'dark' 
               ? 'bg-gray-700 hover:bg-gray-600 text-white' 
               : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
           } shadow-lg hover:shadow-xl`}
         >
           <ArrowLeft className="w-5 h-5 inline mr-2" />
           Annuler
         </button>

         <button
           onClick={handleSaveEvent}
           disabled={loading || !eventData.title || !eventData.dossierId || !eventData.startDate || !eventData.responsableId}
           className={`px-8 py-4 rounded-xl font-medium transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
             `bg-gradient-to-r ${currentAdminStyle.gradient} text-white shadow-lg hover:shadow-xl`
           }`}
         >
           {loading ? (
             <div className="flex items-center space-x-2">
               <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
               <span>Création...</span>
             </div>
           ) : (
             <div className="flex items-center space-x-2">
               <Save className="w-5 h-5" />
               <span>Créer l'événement</span>
               <Download className="w-5 h-5" />
             </div>
           )}
         </button>
       </div>

       {/* Informations sur l'export */}
       <div className={`mt-6 p-4 rounded-lg ${
         theme === 'dark' ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'
       }`}>
         <div className="flex items-center space-x-2">
           <Download className="w-5 h-5 text-blue-600" />
           <span className={`text-sm font-medium ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
             💡 Un fichier .ics sera automatiquement téléchargé pour ajouter l'événement à Google Calendar !
           </span>
         </div>
       </div>
     </div>
   </div>
 );
};

export default AdminAddEvent;